import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Symptom, CheckinType, Meal, MAIN_SYMPTOMS } from '../lib/types';
import { BristolStoolPicker } from './BristolStoolPicker';
import { formatIsoDate, formatShortDate, formatTime } from '../lib/dateUtils';
import { triggerHaptic } from '../lib/haptics';
import { normalizeSymptomScores } from '../lib/storage';
import {
  Activity,
  Link2,
  Trash2,
  Calendar,
  Clock,
} from 'lucide-react';

interface SymptomFormProps {
  onSuccess: (symptom: Omit<Symptom, 'id'>) => void;
  onUpdate?: (symptom: Symptom) => void;
  onDelete?: (id: string) => void;
  initialSymptom?: Symptom | null;
  recentMeals: Meal[];
  defaultCheckinType?: CheckinType;
}

const DEFAULT_SCORES: Record<string, number> = {
  bloating: 0,
  stomach_pain: 0,
  heartburn: 0,
  reflux: 0,
  mouth_taste: 0,
  nausea: 0,
  vomiting: 0,
  diarrhea: 0,
  constipation: 0,
  headache: 0,
  eczema: 0,
  hayfever: 0,
  asthma: 0,
};

export const SymptomForm: React.FC<SymptomFormProps> = ({
  onSuccess,
  onUpdate,
  onDelete,
  initialSymptom,
  recentMeals,
  defaultCheckinType = 'General Daily',
}) => {
  const initDate = initialSymptom?.timestamp_iso ? new Date(initialSymptom.timestamp_iso) : new Date();
  const [entryDate, setEntryDate] = useState(() => formatIsoDate(initDate));
  const [entryTime, setEntryTime] = useState(() => {
    const hh = String(initDate.getHours()).padStart(2, '0');
    const mm = String(initDate.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  });

  const dayMeals = useMemo(() => {
    return recentMeals
      .filter((m) => m.date === entryDate)
      .sort((a, b) => {
        const tA = a.timestamp_iso ? new Date(a.timestamp_iso).getTime() : 0;
        const tB = b.timestamp_iso ? new Date(b.timestamp_iso).getTime() : 0;
        return tB - tA;
      });
  }, [recentMeals, entryDate]);

  const getPrecedingMealId = useCallback(
    (mealsList: Meal[], date: string, time: string) => {
      if (mealsList.length === 0) return null;
      const symptomTimeMs = new Date(`${date}T${time}:00`).getTime();
      const preceding = mealsList.find((m) => {
        const mTime = m.timestamp_iso ? new Date(m.timestamp_iso).getTime() : 0;
        return isNaN(symptomTimeMs) || mTime <= symptomTimeMs;
      });
      return preceding ? preceding.id : mealsList[0].id;
    },
    []
  );

  const [isDirectReaction, setIsDirectReaction] = useState<boolean>(
    Boolean(initialSymptom?.associated_meal_id || initialSymptom?.checkin_type === 'Post-Meal')
  );
  const [associatedMealId, setAssociatedMealId] = useState<string | null>(() => {
    return initialSymptom?.associated_meal_id || null;
  });

  const [scores, setScores] = useState<Record<string, number>>(() => {
    if (initialSymptom) {
      const norm = normalizeSymptomScores(initialSymptom);
      return { ...DEFAULT_SCORES, ...norm.symptom_scores };
    }
    return { ...DEFAULT_SCORES };
  });

  const [overallWellbeing, setOverallWellbeing] = useState(initialSymptom?.overall_wellbeing || 4);
  const [stoolType, setStoolType] = useState<number | null>(initialSymptom?.stool_type ?? null);
  const [notes, setNotes] = useState(initialSymptom?.notes || '');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (initialSymptom) {
      const norm = normalizeSymptomScores(initialSymptom);
      setIsDirectReaction(Boolean(norm.associated_meal_id || norm.checkin_type === 'Post-Meal'));
      setAssociatedMealId(norm.associated_meal_id || null);
      setScores({ ...DEFAULT_SCORES, ...norm.symptom_scores });
      setOverallWellbeing(norm.overall_wellbeing || 4);
      setStoolType(norm.stool_type ?? null);
      setNotes(norm.notes || '');

      const d = new Date(norm.timestamp_iso);
      if (!isNaN(d.getTime())) {
        setEntryDate(formatIsoDate(d));
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        setEntryTime(`${hh}:${mm}`);
      }
    }
  }, [initialSymptom]);

  const handleScoreChange = (symId: string, val: number) => {
    triggerHaptic('selection');
    setScores((prev) => ({ ...prev, [symId]: val }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const timestampIso = `${entryDate}T${entryTime}:00`;
    let derivedCheckinType: CheckinType = defaultCheckinType;
    if (isDirectReaction && associatedMealId) {
      derivedCheckinType = 'Post-Meal';
    }

    const activeTags = MAIN_SYMPTOMS.filter((s) => (scores[s.id] || 0) > 0).map(
      (s) => `${s.name} (${scores[s.id]}/10)`
    );

    const symptomData = {
      timestamp_iso: timestampIso,
      date: entryDate,
      checkin_type: derivedCheckinType,
      associated_meal_id: isDirectReaction ? associatedMealId : null,
      bloating_score: scores.bloating || 0,
      soreness_score: scores.stomach_pain || 0,
      overall_wellbeing: overallWellbeing,
      symptom_scores: scores,
      stool_type: stoolType,
      bad_taste_score: scores.mouth_taste || undefined,
      symptom_tags: activeTags.join(', '),
      notes: notes.trim() || undefined,
    };

    if (initialSymptom && onUpdate) {
      onUpdate({ ...symptomData, id: initialSymptom.id });
    } else {
      onSuccess(symptomData);
    }
    setIsSubmitting(false);
  };

  return (
    <div className="space-y-4 text-left">
      {/* Header if editing */}
      {initialSymptom && (
        <div className="flex items-center justify-between pb-1 border-b border-slate-200 dark:border-slate-800">
          <span className="text-xs font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400">
            Editing Check-In
          </span>
          {onDelete && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="text-xs text-rose-500 hover:text-rose-600 flex items-center space-x-1 cursor-pointer"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span>Delete</span>
            </button>
          )}
        </div>
      )}

      {showDeleteConfirm && initialSymptom && onDelete && (
        <div className="p-3 bg-rose-50 dark:bg-rose-950/60 rounded-2xl border border-rose-200 dark:border-rose-800 space-y-2">
          <p className="text-xs text-rose-700 dark:text-rose-200 font-semibold">
            Delete this check-in?
          </p>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => onDelete(initialSymptom.id)}
              className="px-3 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-bold active:scale-95 transition-all cursor-pointer"
            >
              Confirm Delete
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(false)}
              className="px-3 py-1.5 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-xs cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Main Symptoms with 0-10 Sliders */}
      <div className="space-y-1">
        <div className="flex items-center justify-between pb-1.5 px-1 border-b border-slate-100 dark:border-zinc-800/80">
          <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
            Symptoms
          </span>
          <span className="text-[10px] font-semibold text-slate-400 dark:text-zinc-500">
            0 to 10 Scale
          </span>
        </div>

        <div className="divide-y divide-slate-100 dark:divide-zinc-800/60 max-h-[380px] overflow-y-auto pr-1">
          {MAIN_SYMPTOMS.map((sym) => {
            const currentScore = scores[sym.id] || 0;
            const isMouthTaste = sym.id === 'mouth_taste';
            return (
              <div
                key={sym.id}
                className="py-2.5 flex items-center justify-between gap-2.5 transition-colors"
              >
                {/* Name on the left */}
                <div className="w-24 sm:w-28 shrink-0">
                  <span className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white block leading-tight">
                    {sym.name}
                  </span>
                </div>

                {/* Scale slider to the right of the name */}
                <div className="flex-1 flex flex-col justify-center px-1">
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="1"
                    value={currentScore}
                    onChange={(e) => handleScoreChange(sym.id, parseInt(e.target.value, 10))}
                    className="w-full h-1.5 bg-slate-200 dark:bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-600 dark:accent-blue-500"
                  />
                  <div className="flex justify-between text-[8px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 px-0.5 mt-0.5 select-none">
                    <span>{isMouthTaste ? 'Pleasant' : 'Low'}</span>
                    <span>{isMouthTaste ? 'Unpleasant' : 'High'}</span>
                  </div>
                </div>

                {/* Score to the right of the slider */}
                <div className="w-6 text-right shrink-0">
                  <span
                    className={`text-xs sm:text-sm font-black tabular-nums ${
                      currentScore > 0
                        ? currentScore >= 7
                          ? 'text-rose-600 dark:text-rose-400'
                          : currentScore >= 4
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-blue-600 dark:text-blue-400'
                        : 'text-slate-400 dark:text-zinc-500'
                    }`}
                  >
                    {currentScore}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Overall Wellbeing (1–5) */}
      <div className="space-y-1.5 pt-2 border-t border-slate-100 dark:border-zinc-800/80">
        <div className="flex items-center justify-between px-1">
          <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
            Overall Vitality & Wellbeing
          </label>
          <span className="text-xs font-bold text-slate-700 dark:text-zinc-300">
            {overallWellbeing} / 5
          </span>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {[1, 2, 3, 4, 5].map((score) => (
            <button
              key={score}
              type="button"
              onClick={() => {
                triggerHaptic('selection');
                setOverallWellbeing(score);
              }}
              className={`py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                overallWellbeing === score
                  ? 'bg-slate-950 dark:bg-white text-white dark:text-black font-black shadow-xs'
                  : 'bg-slate-100 dark:bg-zinc-800/80 text-slate-600 dark:text-zinc-400 border border-slate-200 dark:border-zinc-800'
              }`}
            >
              {score}
            </button>
          ))}
        </div>
      </div>

      {/* Bristol Stool Scale Picker */}
      <BristolStoolPicker
        value={stoolType}
        onChange={setStoolType}
      />

      {/* Date & Time Selector */}
      <div className="p-3.5 bg-slate-50 dark:bg-[#0e1017] rounded-2xl border border-slate-200 dark:border-slate-800/80 space-y-2.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center space-x-1.5">
            <Clock className="w-3.5 h-3.5 text-blue-500" />
            <span>Time Recorded</span>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <input
            type="date"
            value={entryDate}
            onChange={(e) => {
              if (e.target.value) setEntryDate(e.target.value);
            }}
            className="w-full px-3 py-2 bg-white dark:bg-[#141724] border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-800 dark:text-slate-100"
          />
          <input
            type="time"
            value={entryTime}
            onChange={(e) => {
              if (e.target.value) setEntryTime(e.target.value);
            }}
            className="w-full px-3 py-2 bg-white dark:bg-[#141724] border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-800 dark:text-slate-100"
          />
        </div>
      </div>

      {/* Possible Direct Reaction Meal Link */}
      <div className="pt-2 border-t border-slate-200/80 dark:border-slate-800/80 space-y-2">
        <label className="flex items-center space-x-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isDirectReaction}
            onChange={(e) => {
              const checked = e.target.checked;
              setIsDirectReaction(checked);
              if (checked) {
                const autoId = getPrecedingMealId(dayMeals, entryDate, entryTime);
                setAssociatedMealId(autoId);
              } else {
                setAssociatedMealId(null);
              }
            }}
            className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300 dark:border-slate-700"
          />
          <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
            Associate with meal logged today
          </span>
        </label>

        {isDirectReaction && dayMeals.length > 0 && (
          <select
            value={associatedMealId || ''}
            onChange={(e) => setAssociatedMealId(e.target.value || null)}
            className="w-full px-3 py-2 bg-white dark:bg-[#0a0c14] border border-slate-300 dark:border-slate-700 rounded-xl text-xs text-slate-800 dark:text-slate-200"
          >
            {dayMeals.map((m) => (
              <option key={m.id} value={m.id}>
                {m.meal_type}: {m.description} ({formatTime(m.timestamp_iso)})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Free-form Notes for extra sensations and triggers */}
      <div className="space-y-1">
        <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-zinc-400 block">
          Notes (Additional details, custom triggers, context)
        </label>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add extra context, specific sensations, or meal triggers..."
          className="w-full px-3 py-2.5 bg-white dark:bg-[#12141c] border border-slate-300 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
        />
      </div>

      {/* Submit Button */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isSubmitting}
        className="w-full py-3 bg-slate-950 dark:bg-white text-white dark:text-black rounded-2xl text-xs font-black shadow-md active:scale-95 transition-all cursor-pointer"
      >
        {initialSymptom ? 'Save Changes' : 'Save Symptom Check-In'}
      </button>
    </div>
  );
};
